/* eslint-disable camelcase */
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react'
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete'
import styled, { ThemeContext } from 'styled-components'
// import Head from 'next/head'
import { v4 as uuid } from 'uuid'

import ErrorBox from 'components/ErrorBox'
import PasswordInput from 'components/GlobalStyles/Inputs/PasswordInput'
import TextInput from 'components/GlobalStyles/Inputs/TextInput'
import RoundedButton from 'components/RoundedButton'
import Checkmark from 'components/SVG/Checkmark'
import FlaredArrow from 'components/SVG/FlaredArrow'

import { trackSignup, trackZipNotCovered } from 'services/analytics'
import { QueryParamsContext } from 'services/contexts'
import { sendIntercomNewListingEvent } from 'services/intercom'
import { createListing, getListingByAddress } from 'services/listings'
import { useUser } from 'services/swr/useUser'
import { createUser } from 'services/users'
import { validateZip } from 'services/zips'

import formatLocation from 'utils/formatLocation'
import { isChrome } from 'utils/userAgent'
import validateEmail from 'utils/validateEmail'

import { ICreateListingData, ICreateUserData, ISignupParams } from 'types'
import { ButtonType } from 'types/enums'

const MIN_PASSWORD_LENGTH = 8

const validatePassword = (password: string) => password.length >= MIN_PASSWORD_LENGTH

const FullWidthContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
`

const MaxWidthContainer = styled.div`
  flex-basis: 750px;
  flex-shrink: 1;
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`

interface ISizeProps {
  size?: number
}
const Spacer = styled.div<ISizeProps>`
  width: ${props => props.size || 20}px;
  height: ${props => props.size || 20}px;
`

const Grid = styled.div`
  /* IE 11 base */
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
`

interface IFlexBasisProps {
  flexBasis?: number
}
interface ISpacerProps {
  spacer?: boolean
}

const Form = styled.form``

const InputContainer = styled.div<IFlexBasisProps & ISpacerProps>`
  /* IE 11 base */
  flex-basis: ${props => `calc(${props.flexBasis || 50}% - ${props.spacer ? 10 : 0}px)`};
  flex-grow: 0;
  margin-bottom: 10px;

  @media (max-width: ${props => props.theme.metrics.phone}px) {
    flex-basis: 100%;
  }
`

interface IErrorProps {
  error: boolean
}
interface IInvalidProps {
  invalid?: boolean
}
interface IDropdownOpenProps {
  dropdownOpen?: boolean
}
const Input = styled.input.attrs({ type: 'text' })<
  IErrorProps & IInvalidProps & IDropdownOpenProps
>`
  padding-top: 2px; /* font correction */
  padding-left: 10px;
  background: ${props => props.theme.colors.transparentGrey30Alpha50};
  ${props => props.error && `background: ${props.theme.colors.transparentRedAlpha10};`}
  border: ${props => `1px solid ${props.theme.colors[props.error ? 'red' : 'grey30']}`};
  border-radius: 4px;
  width: 100%;
  height: 50px;

  font-weight: 500;
  font-size: 16px;
  line-height: 22px;

  caret-color: ${props => props.theme.colors.blue};
  color: ${props => props.theme.colors.grey27};

  &::placeholder {
    color: ${props => props.theme.colors.grey27};
    opacity: 1;
  }
  &::-webkit-input-placeholder {
    color: ${props => props.theme.colors.grey27};
    opacity: 1;
  }

  &:focus {
    outline: none;
    border: 1px solid ${props => props.theme.colors[props.invalid ? 'red' : 'blue']};
    ${props =>
    props.dropdownOpen
      && `
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
  }
`

const PlaceInputContainer = styled.div`
  position: relative;
`

const DropdownContainer = styled.div`
  position: absolute;
  top: 38px;
  left: 0;
  background-color: ${props => props.theme.colors.blue};
  border: none;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  width: 100%;
  z-index: 100;
  margin: 10px 0px;
  text-align: left;
  letter-spacing: 1px;
  font-size: 16px;

  display: none;

  ${Input}:focus + & {
    display: block;
  }
`

const AutocompleteOption = styled.div`
  padding: 10px 10px 0px;
  font-style: normal;
  font-weight: 900;
  font-size: 11px;
  line-height: 15px;
  letter-spacing: normal;
  color: ${props => props.theme.colors.white};

  &:last-child {
    padding-bottom: 10px;
  }

  cursor: pointer;
`

const CoveredContainer = styled.div`
  display: flex;
  padding: 10px;
`

const CheckmarkContainer = styled.div`
  margin-right: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background: ${props => props.theme.colors.yellow};
`

const CoveredText = styled.div`
  font-style: normal;
  font-weight: 900;
  font-size: 11px;
  line-height: 15px;
  color: ${props => props.theme.colors.white};
`

const InputLabel = styled.label`
  display: block;
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  letter-spacing: 1.25px;
  text-align: left;
`

const SmallText = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;

  & > a {
    color: ${props => props.theme.colors.blue};
  }
`

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const COVERED_TEXT = 'You are in our coverage area!'
const NOT_COVERED_TEXT = 'You are not in our coverage area'

const NO_GOOGLE_MATCH
  = 'Please retype the address and select one of the Google Maps suggestions. If none of the options match your address or there are no suggestions,'

const DUPLICATE_EMAIL = 'An account already exists for this email address.'

const placesAutocompleteSearchOptions = {
  componentRestrictions: { country: 'us' },
}

const initialListingData: ICreateListingData = {
  aptNum: '',
  city: '',
  country: '',
  county: '',
  latitude: 0,
  longitude: 0,
  state: '',
  street: '',
  zip: '',
}

const initialCreateUserParams: ISignupParams = {
  user: {
    name: '',
    email: '',
    password: '',
  },
  listing: initialListingData,
}

interface IListingErrorState {
  id?: string
  message?: string
}

const initialListingErrorState: IListingErrorState = {
  id: '',
  message: '',
}

interface ICompletedState {
  name: boolean
  email: boolean
  password: boolean
  listing: boolean
  [key: string]: boolean
}

const initialCompletedState: ICompletedState = {
  name: false,
  email: false,
  password: false,
  listing: false,
}

interface IInvalidState extends ICompletedState {}

const initialInvalidState: IInvalidState = initialCompletedState

interface IProps {
  buttonColor?: string
  buttonWidth?: number
}

const CreateListing: FunctionComponent<IProps> = ({ buttonColor, buttonWidth }) => {
  // theme
  const theme = useContext(ThemeContext)

  const renderIcon = () => (
    <FlaredArrow
      fill={theme.colors.white}
      width={20}
      height={14}
    />
  )

  // router
  const router = useRouter()

  // user (to check if logged in)
  const { user } = useUser()

  // url ISignupParams
  const { params: query } = useContext(QueryParamsContext)

  // error state
  const [error, setError] = useState('')
  const [zipError, setZipError] = useState('')
  const [listingError, setListingError] = useState<IListingErrorState>(initialListingErrorState)

  // form state
  const [autoComplete, setAutoComplete] = useState('off')
  const [completedState, setCompletedState] = useState(initialCompletedState)
  const [invalidState, setInvalidState] = useState(initialInvalidState)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    const mode = isChrome() ? 'nope' : 'off'
    setAutoComplete(mode)
  }, [isChrome])

  const resetErrors = () => {
    if (error) setError('')

    if (zipError) setZipError('')

    if (submitted) setSubmitted(false)

    if (listingError) setListingError(initialListingErrorState)
  }

  useEffect(() => {
    if (error) setSubmitted(false)
  }, [error])

  // user
  const [userData, setUserData] = useState<ICreateUserData>(initialCreateUserParams.user)

  const updateUser = (field: string, value: string) => {
    if (error) setError('')

    if (submitted) setSubmitted(false)

    setUserData({ ...userData, [field]: field === 'email' ? value.trim() : value })
    setCompletedState({ ...completedState, [field]: !!value })
  }

  // listing
  const [listingData, setListingData] = useState<ICreateListingData>(
    initialCreateUserParams.listing,
  )

  const updateListingProperty = (field: string, value: string | number) => {
    if (submitted) setSubmitted(false)

    setListingData({ ...listingData, [field]: value })
  }

  const updateListingObj = async (geocoderResult: google.maps.GeocoderResult) => {
    if (!geocoderResult) return setInvalidState({ ...invalidState, listing: true })

    const locationData: ICreateListingData | {} = await formatLocation(geocoderResult)
    setListingData({ ...listingData, ...locationData })
  }

  // displayed address
  const [address, setAddress] = useState('')
  const [addressSelected, setAddressSelected] = useState(false)

  const handleAddressChange = (value: string) => {
    setAddress(value)
    setAddressSelected(false)
    resetErrors()
  }

  const handleAddressSelect = async (selectedAddress: string) => {
    try {
      const results = await geocodeByAddress(selectedAddress)

      if (results && !!results[0]) {
        updateListingObj(results[0])
        setAddress(results[0]?.formatted_address)
        setAddressSelected(true)
      }
    } catch (err) {
      setError("We couldn't look up on your address right now. Please try again later.")
    }
  }

  // covered
  const [covered, setCovered] = useState(false)

  const getCovered = useCallback(
    async (zip: string) => {
      const validZip = await validateZip(zip)
      setCovered(validZip)
      setInvalidState({ ...invalidState, listing: !validZip })
      setCompletedState({ ...completedState, listing: validZip })
      setZipError(validZip ? '' : 'Your area is not yet covered,')
      if (!validZip) {
        trackZipNotCovered(listingData, user || userData, router.query)
        setListingData(initialListingData)
        setAddress('')
      }
    },
    [completedState, listingData],
  )

  useEffect(() => {
    if (!listingData.zip) return

    getCovered(listingData.zip)
  }, [listingData.zip])

  const handleSignup = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitted(true)

    // check if user picked a value from Google Places Autocomplete dropdown
    if (!addressSelected) return setError(NO_GOOGLE_MATCH)

    // check if all fields are complete and valid
    if (
      Object.keys(completedState).every(key => !!completedState[key])
      && Object.keys(invalidState).every(key => !invalidState[key])
    ) {
      // check if email is valid
      if (!validateEmail(userData.email)) return setError('Email address is invalid')

      // check if password length is greater than 8 characters
      if (!validatePassword(userData.password)) {
        return setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      }

      const [newListing, createError] = await createUser({
        listing: listingData,
        user: userData,
      })
      if (createError?.message) return setError(createError.message)

      if (newListing) {
        trackSignup(newListing, user || userData, router.query)
        sendIntercomNewListingEvent(newListing, router.query)
        return router.push({
          pathname: `/listing/${newListing._id}/create/welcome`,
          query: { ...query, fromSignup: 'true' } as { [key: string]: string },
        })
      }

      // base case: shape of createUser return is `[undefined, undefined]`
      return setError('Something went wrong. Please try refreshing the page.')
    }

    setError('Some required fields were not filled in.')
  }

  const handleCreateListing = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!user) return

    setSubmitted(true)
    if (completedState.listing && !invalidState.listing) {
      const [newListing, createError] = await createListing({
        ...listingData,
        userId: user._id,
      })

      if (createError.message) {
        setSubmitted(false)
        return createError.status === '409'
          ? setListingError({
            message: createError.message,
            id: (await getListingByAddress({ ...listingData, userId: user._id }))?.id,
          })
          : setError(createError.message || 'Something went wrong')
      }

      if (newListing) {
        return router.push({
          pathname: `/listing/${newListing._id}/create/welcome`,
          query: query as { [key: string]: string },
        })
      }
    }

    setError('Missing an address')
  }

  // google maps script
  // const [loadMapScript, setLoadMapScript] = useState(false)
  // useEffect(() => {
  //   setTimeout(() => {
  //     if (typeof window !== 'undefined' && !window.google) setLoadMapScript(true)
  //   }, 1000)
  // }, [])

  // const renderHead = () => (
  //   <Head>
  //     <script
  //       async
  //       defer
  //       src={`https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_API_KEY}&libraries=places&callback=mapsCallback`}
  //     />
  //   </Head>
  // )

  return (
    <Form onSubmit={user ? handleCreateListing : handleSignup}>
      <FullWidthContainer>
        {/* {loadMapScript && renderHead()} */}
        <MaxWidthContainer>
          <Grid>
            <InputContainer
              flexBasis={75}
              spacer
            >
              <InputLabel>
                Property Address
                <Spacer size={5} />
                <PlacesAutocomplete
                  value={address}
                  onChange={handleAddressChange}
                  onSelect={handleAddressSelect}
                  searchOptions={placesAutocompleteSearchOptions}
                  shouldFetchSuggestions={address.length > 2}
                >
                  {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <PlaceInputContainer>
                      {/*
                    // @ts-ignore */}
                      <Input
                        data-testid="propertyAddress"
                        {...getInputProps({
                          className: 'street-address',
                        })}
                        dropdownOpen={!!suggestions?.length || loading || completedState.covered}
                        error={(!!error && !addressSelected) || (!!zipError && !covered)}
                        name={uuid()} // force browser not to suggest previous inputs
                        autoComplete={autoComplete}
                        disabled={submitted}
                      />
                      <DropdownContainer>
                        {loading && null}
                        {suggestions.map(suggestion => (
                          <AutocompleteOption {...getSuggestionItemProps(suggestion)}>
                            {suggestion.description}
                          </AutocompleteOption>
                        ))}
                        {completedState.covered && !suggestions.length && !!address && (
                          <CoveredContainer>
                            {covered && (
                              <CheckmarkContainer>
                                <Checkmark
                                  width={8}
                                  height={6}
                                  fill={theme.colors.black}
                                />
                              </CheckmarkContainer>
                            )}
                            <CoveredText>{covered ? COVERED_TEXT : NOT_COVERED_TEXT}</CoveredText>
                          </CoveredContainer>
                        )}
                      </DropdownContainer>
                    </PlaceInputContainer>
                  )}
                </PlacesAutocomplete>
              </InputLabel>
            </InputContainer>
            <InputContainer
              flexBasis={25}
              spacer
            >
              <InputLabel>
                Unit #
                <Spacer size={5} />
                <TextInput
                  name="aptNum"
                  onChange={updateListingProperty}
                  onChangeImmediate={resetErrors}
                  delay={0}
                  value={listingData.aptNum}
                  inputHeight={50}
                  autoComplete={autoComplete}
                  disabled={submitted}
                />
              </InputLabel>
            </InputContainer>
            {!user && (
              <InputContainer flexBasis={100}>
                <InputLabel>
                  Name
                  <Spacer size={5} />
                  <TextInput
                    name="name"
                    onChange={updateUser}
                    onChangeImmediate={resetErrors}
                    delay={0}
                    value={userData.name}
                    submissionError={!!error && !userData.name}
                    inputHeight={50}
                    autoComplete={autoComplete}
                    disabled={submitted}
                  />
                </InputLabel>
              </InputContainer>
            )}
            {!user && (
              <InputContainer spacer>
                <InputLabel>
                  Email
                  <Spacer size={5} />
                  <TextInput
                    name="email"
                    type="email"
                    onChange={updateUser}
                    onChangeImmediate={resetErrors}
                    delay={0}
                    value={userData.email}
                    submissionError={!!error && !validateEmail(userData.email)}
                    inputHeight={50}
                    autoComplete={autoComplete}
                    disabled={submitted}
                  />
                </InputLabel>
              </InputContainer>
            )}
            {!user && (
              <InputContainer spacer>
                <InputLabel>
                  Password
                  <Spacer size={5} />
                  <PasswordInput
                    name="password"
                    onChange={updateUser}
                    value={userData.password}
                    background={theme.colors.transparentGrey30Alpha50}
                    error={!!error && !userData.password}
                    submissionError={!!error && !validatePassword(userData.password)}
                    inputHeight={50}
                    disabled={submitted}
                    placeholder=" "
                    textColor={theme.colors.grey27}
                    borderColor={theme.colors.grey30}
                  />
                </InputLabel>
              </InputContainer>
            )}
          </Grid>
          <Spacer size={12} />
          {!!error && (
            <>
              <ErrorBox
                error={error}
                showChatMessage={!completedState.listing || error === NO_GOOGLE_MATCH}
                chatMessage="please chat with us for help."
                showLinkMessage={error === DUPLICATE_EMAIL}
                linkText="Click here to Sign In"
                linkUri="/signin"
                prepopulatedIntercomMessage="None of the Google Maps suggestions match my address/there are no suggestions."
              />
              <Spacer size={32} />
            </>
          )}
          {!!zipError && (
            <>
              <ErrorBox
                error={zipError}
                showLinkMessage
                linkText="please see here for a list of states that we currently cover."
                linkUri="/licensing"
              />
              <Spacer size={32} />
            </>
          )}
          {!!listingError.message && !!listingError.id && (
            <>
              <ErrorBox
                error={listingError.message}
                showLinkMessage
                linkText="Click here to edit it"
                linkUri={`/listing/${listingError.id}/create/welcome`}
              />
              <Spacer size={32} />
            </>
          )}
          <ButtonContainer>
            <RoundedButton
              type={ButtonType.submit}
              background={buttonColor || theme.colors.blue4}
              color={theme.colors.white}
              disabled={!!error}
              radius={20}
              width={buttonWidth || 300}
              text={user ? 'Create Listing' : 'Create My Free Account'}
              renderRightIcon={renderIcon}
              loading={submitted}
            />
          </ButtonContainer>
          <Spacer size={10} />
          <SmallText>
            {'By creating an account, I agree to the '}
            <Link href="/terms-of-use">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a>Terms &amp; Conditions.</a>
            </Link>
          </SmallText>
          <Spacer size={5} />
          <SmallText>
            {!user && 'Already have an account? '}
            {!user && (
              <Link href="/signin">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a>Sign in here.</a>
              </Link>
            )}
          </SmallText>
        </MaxWidthContainer>
      </FullWidthContainer>
    </Form>
  )
}

export default CreateListing
